import { Fragment, useEffect, useMemo } from 'react';
import { PanelControls } from '~/components/ui/PanelControls';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction, InputKey, VehicleTuningSubcategory } from '~/shared';
import { useTuningCategories } from '../../hooks';
import {
	setActiveIndex,
	setActiveIndexNext,
	setActiveIndexPrev,
	setActiveStage,
	setActiveTuningCategory,
	setActiveTuningGroup,
	setActiveTuningSubcategory,
	setMaxIndex,
	TuningStudioStage,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	TUNING_GROUP_CATEGORY,
	TUNING_GROUP_SUBCATEGORIES,
	TUNING_SUBCATEGORY_ICONS,
	TUNING_SUBCATEGORY_LABELS,
	VEHICLE_TUNING_CATEGORY_LABELS,
} from '../../tuningGroups.model';
import { Categories } from '../Categories';
import { StyledContainer, StyledControlsContainer } from '../Main/Main.styled';
import { MenuItem } from '../MenuItem';

interface ISubcategoryEntry {
	id: VehicleTuningSubcategory;
	name: string;
	icon: string;
	count: number;
}

const SubcategoryMenu = () => {
	const dispatch = useLayoutDispatch();
	const activeIndex = useLayoutSelector(
		(state) => state.tuningStudio.activeIndex,
	);
	const activeTuningGroup = useLayoutSelector(
		(state) => state.tuningStudio.activeTuningGroup,
	);
	const modalState = useObservable(rage.modal.getCurrentModal());
	const interfaceHidden = useLayoutSelector(
		(state) => state.tuningStudio.interfaceHidden,
	);
	const { shopItems } = useTuningCategories();

	const itemsBySubcategory = useMemo(() => {
		const map = new Map<
			VehicleTuningSubcategory,
			{ total: number; nonStock: number }
		>();
		shopItems.forEach((item) => {
			if (!item.subcategoryId) {
				return;
			}
			const entry = map.get(item.subcategoryId) ?? { total: 0, nonStock: 0 };
			entry.total += 1;
			if (!item.id.startsWith('stock_')) {
				entry.nonStock += 1;
			}
			map.set(item.subcategoryId, entry);
		});
		return map;
	}, [shopItems]);

	const entries = useMemo<ISubcategoryEntry[]>(() => {
		if (!activeTuningGroup) {
			return [];
		}
		const cats = TUNING_GROUP_SUBCATEGORIES[activeTuningGroup];
		const result: ISubcategoryEntry[] = [];
		cats.forEach((id) => {
			const counts = itemsBySubcategory.get(id) ?? { total: 0, nonStock: 0 };
			if (counts.nonStock === 0) {
				return;
			}
			const count = counts.total;
			result.push({
				id,
				name: TUNING_SUBCATEGORY_LABELS[id] ?? id,
				icon: TUNING_SUBCATEGORY_ICONS[id] ?? '',
				count,
			});
		});
		return result;
	}, [activeTuningGroup, itemsBySubcategory]);

	const handleBack = () => {
		rage.vehicleTuningShop
			.removeTemporaryItems()
			.catch((err) => rage.notifications.showException(err));
		rage.vehicleTuningShop
			.resetWheelsSettings()
			.catch((err) => rage.notifications.showException(err));

		dispatch(setActiveTuningCategory(null));
		dispatch(setActiveTuningSubcategory(null));
		dispatch(setActiveTuningGroup(null));
		dispatch(setActiveStage(TuningStudioStage.EXTERIOR_MENU));
		dispatch(setActiveIndex(0));
	};

	const handleSelect = (index: number) => {
		const entry = entries[index];
		if (!entry) {
			return;
		}
		dispatch(setActiveTuningCategory(null));
		dispatch(setActiveTuningSubcategory(entry.id));
		dispatch(setActiveIndex(0));
		dispatch(setActiveStage(TuningStudioStage.TUNING_MENU));
	};

	const leftControls = useMemo(
		() => [
			{
				text: 'Управление',
				keys: [InputKey.ArrowLeft, InputKey.ArrowRight],
			},
			{
				text: 'Выбрать подкатегорию',
				keys: [InputKey.Enter],
				onClick: () => handleSelect(activeIndex),
			},
			{
				text: 'Крутить камеру',
				keys: [InputKey.MouseRight],
			},
		],
		[activeIndex, entries],
	);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () => rage.vehicleTuningShop.exitMenu(),
		},
	];

	useInputAction(InputAction.CommonMenuNavLeft, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		dispatch(setActiveIndexPrev());
	});

	useInputAction(InputAction.CommonMenuNavRight, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		dispatch(setActiveIndexNext());
	});

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		handleSelect(activeIndex);
	});

	useInputAction(InputAction.CommonMenuClose, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		handleBack();
	});

	useEffect(() => {
		const maxIndex = Math.max(0, entries.length - 1);
		dispatch(setMaxIndex(maxIndex));
	}, [entries, dispatch]);

	const groupName = activeTuningGroup
		? VEHICLE_TUNING_CATEGORY_LABELS[TUNING_GROUP_CATEGORY[activeTuningGroup]]
		: '';

	return (
		<Fragment>
			<StyledContainer>
				<Categories
					name={groupName}
					activeIndex={activeIndex}
					totalCount={entries.length}
					handleBack={handleBack}
					handlePrev={() => dispatch(setActiveIndexPrev())}
					handleNext={() => dispatch(setActiveIndexNext())}
				>
					{entries.map((entry, index) => (
						<MenuItem
							type="menu"
							name={entry.name}
							icon={entry.icon}
							count={entry.count}
							key={entry.id}
							onClick={() => handleSelect(index)}
							active={activeIndex === index}
							id={`subcategoryMenuItem#${index}`}
						/>
					))}
				</Categories>
			</StyledContainer>
			<StyledControlsContainer>
				<PanelControls controls={leftControls} align="left" />
				<PanelControls controls={rightControls} align="right" />
			</StyledControlsContainer>
		</Fragment>
	);
};

export { SubcategoryMenu };
